import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Icon from '../../atoms/Icon'
import PopoverMenu from './PopoverMenu'
import SelectButton from './SelectButton'

function useOptionsFilter (searchText, options, pinnedOptions) {
  return useMemo(() => {
    try {
      const filteredPinned = (pinnedOptions || []).filter(x => x.label.toLowerCase().includes(searchText.toLowerCase()))
      const filteredOptions = (options || []).filter(x => x.label.toLowerCase().includes(searchText.toLowerCase()))

      return [filteredOptions, filteredPinned]
    } catch (err) {
      return [[], []]
    }
  }, [searchText, options, pinnedOptions])
}

function ItemSelect ({
  options,
  pinnedOptions,
  value,
  showIcon,
  hideSearch,
  onSelect,
  isLoading,
  className,
  style
}) {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [filteredOptions, filteredPinnedOptions] = useOptionsFilter(searchText, options, pinnedOptions)
  const selectedOption = useMemo(() => {
    return (options || []).find(x => x.value === value) || (pinnedOptions || []).find(x => x.value === value)
  }, [options, pinnedOptions, value])

  const handleToggle = useCallback(() => {
    setOpen(p => !p)
    setSearchText('')
  }, [setOpen, setSearchText])

  const handleSelect = useCallback((scope) => {
    onSelect(scope)
    setOpen(false)
  }, [onSelect, setOpen])

  return (
    <>
      <SelectButton
        ref={anchorRef}
        open={open}
        onClick={handleToggle}
        style={style}
        className={className}
        isLoading={isLoading}
      >
        <div className='font-size-sm flex flex-row gap-xs items-center'>
          {(showIcon && selectedOption?.icon) ? (
            <span className='pt-1'><Icon name={selectedOption.icon} customSize={24} /></span>
          ) : null}
          <div>{selectedOption?.label ?? 'Select an Option'}</div>
        </div>
      </SelectButton>
      <PopoverMenu
        anchorEl={anchorRef.current}
        open={open}
        setOpen={setOpen}
        options={filteredOptions}
        pinnedOptions={filteredPinnedOptions}
        onSearchChange={setSearchText}
        onSelect={handleSelect}
        showIcon={showIcon}
        hideSearch={hideSearch}
      />
    </>
  )
}

ItemSelect.propTypes = {
  /** The list of options to be shown */
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any,
    label: PropTypes.string
  })),

  /** The list of options that should be pinned to the top */
  pinnedOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any,
    label: PropTypes.string
  })),

  /** The selected value */
  value: PropTypes.any,

  /** Show icon from option.icon */
  showIcon: PropTypes.bool,

  /** Hide search bar */
  hideSearch: PropTypes.bool,

  /** Callback when item is selected. Will pass back entire option */
  onSelect: PropTypes.func,

  /** Show a loading indicator */
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object
}

export default ItemSelect
